import { PAPEIS_NUMERO } from '@/lib/constants';

/**
 * Canais do inbox — as três entradas de "Conversas" no menu.
 *
 * Um canal não é uma coleção nova: é uma VISTA sobre o que já está gravado em
 * cada conversa. `repositorio.js:garantirConversa` grava `espaco` e
 * `numeroPapel` desde a Fase 2, então separar Leads × Resgate × Recepção é só
 * escolher espaço e filtrar papel — nada muda no servidor.
 *
 * Fonte única para os três lugares que precisam do mapa: a rota (App.jsx), o
 * menu (AppShell.jsx) e o provider. Espalhar isso seria a garantia de que um
 * dia o menu aponta para um canal que a rota não conhece.
 *
 * Não confundir com `services/canaisService.js`, que administra os NÚMEROS da
 * clínica (cadastro e QR Code). Aqui é a leitura do inbox.
 */
/**
 * ★ VT (decisão do dono, 19/set/2026) — RÓTULOS × IDS.
 *
 * Enquanto a API oficial da Meta não entra, a equipe trabalha no número da
 * Evolution (canal `resgate`, `numeroPapel` `marketing_resgate`). Por isso, NA
 * TELA: o canal `resgate` chama-se "Conversas — Leads" (é onde os leads estão) e
 * o canal `leads` (número oficial, ainda sem conversa) chama-se "Conversas — API
 * Oficial" e só o gestor o vê. Os IDS, ROTAS, `numeroPapel` e chaves de
 * `localStorage` NÃO mudam — rótulo é tela; id é contrato. Quando a API oficial
 * for conectada, o dono decide se os rótulos voltam ou se a caixa oficial
 * reabre para a equipe. A palavra "resgate" também saiu dos textos de tela: o
 * número passou a ser chamado de "número da Evolution".
 */
export const CANAIS_CONVERSA = {
  leads: {
    id: 'leads',
    rotulo: 'Conversas — API Oficial',
    // Descrição do canal (campo hoje não renderizado; mantido para o futuro cabeçalho).
    contexto: 'API Oficial da Meta — número de entrada dos anúncios (ainda não conectado)',
    espaco: 'vendas',
    numeroPapel: PAPEIS_NUMERO.marketing_entrada.id,
    // VT: só o gestor, enquanto o número oficial não existe — a equipe não deve ver uma caixa vazia.
    papeis: ['gestor'],
    /**
     * A via oficial cobra por conversa iniciada pela empresa e exige
     * mensagem-modelo aprovada pela Meta. Enquanto isso não existe, oferecer o
     * botão só produziria uma conversa aberta e uma mensagem recusada no envio.
     */
    permiteIniciar: false,
  },
  resgate: {
    id: 'resgate',
    rotulo: 'Conversas — Leads',
    contexto: 'Número de Vendas — leads e reativação',
    espaco: 'vendas',
    numeroPapel: PAPEIS_NUMERO.marketing_resgate.id,
    papeis: ['gestor', 'sdr'],
    // É o motivo de existir do canal: a lista de quem não agendou é a lista de
    // quem NÃO escreveu primeiro.
    permiteIniciar: true,
  },
  recepcao: {
    id: 'recepcao',
    rotulo: 'Conversas — Recepção',
    contexto: 'Pacientes atuais e confirmações de consulta',
    espaco: 'recepcao',
    /**
     * Sem filtro de papel, de propósito: o roteamento manda para a recepção
     * todo telefone que já é de paciente, mesmo entrando por número comercial
     * (PLANO 5.4). Filtrar por `numeroPapel === 'recepcao'` esconderia
     * exatamente essas conversas — as que a recepcionista mais precisa ver.
     */
    numeroPapel: null,
    /**
     * ★ VU (19/set/2026, PERMANENTE): o papel `sdr` atende a recepção — vê e
     * responde estas conversas como a recepcionista. É PAPEL, não função
     * (`funcoes` da VP não abre porta). Espelho das rules (`atendeRecepcao`) e
     * das functions (`PAPEIS_ATENDEM_RECEPCAO`).
     */
    papeis: ['gestor', 'sdr', 'recepcao'],
    permiteIniciar: true,
  },
};

export const canalPorId = (id) => CANAIS_CONVERSA[id] ?? null;

/**
 * Em qual canal do inbox uma conversa APARECE — o inverso de `filtrarPorCanal`.
 *
 * Usado pelo "Abrir conversa" do painel do lead: para selecionar a conversa é
 * preciso navegar para o canal que a lista. Recepção sempre `recepcao`; vendas
 * pelo papel do número (entrada → `leads`, o resto → `resgate`).
 */
export function canalDaConversa(conversa) {
  if (!conversa) return null;
  if (conversa.espaco === 'recepcao') return 'recepcao';
  return conversa.numeroPapel === PAPEIS_NUMERO.marketing_entrada.id ? 'leads' : 'resgate';
}

/**
 * Legenda extra do canal, quando o número por trás dele está fazendo algo além
 * do papel — ou `null` quando não há o que avisar.
 *
 * Hoje há um caso: o canal `resgate` com `criaLead: true` em algum número desse
 * papel (a flag que faz o número da Evolution criar lead novo enquanto o
 * oficial não está conectado). A legenda some sozinha quando a flag é
 * desligada — a lista vem do listener de `numeros`. VT: o texto não nomeia
 * caixa nem número ("Cria lead novo a cada contato").
 */
export function legendaDoCanal(canal, numeros = []) {
  if (canal?.id !== 'resgate') return null;
  const criaLead = numeros.some(
    (n) => n?.papel === PAPEIS_NUMERO.marketing_resgate.id && n?.criaLead === true,
  );
  return criaLead ? 'Cria lead novo a cada contato' : null;
}

/**
 * Canal em que cada papel abre o inbox.
 *
 * Recepcionista na recepção; vendas no resgate, que é o canal com tráfego real
 * hoje — Leads só terá conversa quando o número oficial da Meta estiver de pé,
 * e abrir numa lista vazia pareceria defeito.
 */
export const canalPadrao = (papel) => (papel === 'recepcao' ? 'recepcao' : 'resgate');

/** Papel pode abrir este canal? Superadmin enxerga tudo, como no resto do app. */
export const papelPodeNoCanal = (canal, papel) =>
  papel === 'superadmin' || Boolean(canal?.papeis.includes(papel));

/**
 * Recorta a lista do espaço para o canal.
 *
 * Filtro no cliente: a query por espaço não muda, e o volume de uma clínica não
 * justifica um índice a mais. Conversa antiga sem `numeroPapel` (anterior ao
 * campo) fica fora dos canais de vendas — é o mesmo critério do resto da tela,
 * e some do inbox em vez de aparecer no canal errado.
 */
export function filtrarPorCanal(conversas, canal) {
  if (!canal?.numeroPapel) return conversas;
  return conversas.filter((c) => c.numeroPapel === canal.numeroPapel);
}

/**
 * Papel do NÚMERO por onde o canal fala.
 *
 * Não é o mesmo que `canal.numeroPapel`, e a diferença é a recepção: lá o
 * `numeroPapel` é nulo de propósito (a lista não filtra por papel, porque
 * paciente cai na recepção vindo de qualquer número), mas para FALAR é preciso
 * escolher um número — e ali é o da recepção.
 *
 * Usado por quem parte da tela para o servidor: iniciar conversa e, no
 * emulador, simular mensagem.
 */
export const papelDoNumeroDoCanal = (canal) =>
  canal?.numeroPapel ?? (canal?.espaco === 'recepcao' ? 'recepcao' : 'marketing_entrada');

/** O canal aceita conversa iniciada pela clínica? */
export const podeIniciarConversa = (canal) => Boolean(canal?.permiteIniciar);
